/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AuthCodeExchangeRequest } from '../models/AuthCodeExchangeRequest';
import type { ChangeUserPassword } from '../models/ChangeUserPassword';
import type { ChangeUserPasswordRequest } from '../models/ChangeUserPasswordRequest';
import type { CodeLoginPushRequest } from '../models/CodeLoginPushRequest';
import type { DevicePushTokenRequest } from '../models/DevicePushTokenRequest';
import type { ExtendedTokenObtain } from '../models/ExtendedTokenObtain';
import type { IdTokenExchangeRequest } from '../models/IdTokenExchangeRequest';
import type { LoginFirstStepSchema } from '../models/LoginFirstStepSchema';
import type { LoginPushRequest } from '../models/LoginPushRequest';
import type { LoginSecondStepSchema } from '../models/LoginSecondStepSchema';
import type { MFAMethodCodeRequest } from '../models/MFAMethodCodeRequest';
import type { PaginatedTrenchUserActiveMethodsSchemaList } from '../models/PaginatedTrenchUserActiveMethodsSchemaList';
import type { PasswordResetRequest } from '../models/PasswordResetRequest';
import type { PasswordResetRequestRequest } from '../models/PasswordResetRequestRequest';
import type { PxtraUserPasswordReset } from '../models/PxtraUserPasswordReset';
import type { RecoveryRequestRequest } from '../models/RecoveryRequestRequest';
import type { RegisterCompleteRequest } from '../models/RegisterCompleteRequest';
import type { RegisterEmailConfirmRequest } from '../models/RegisterEmailConfirmRequest';
import type { RegisterEmailRequest } from '../models/RegisterEmailRequest';
import type { RegisterEmailResponse } from '../models/RegisterEmailResponse';
import type { SetNewUserPasswordRequest } from '../models/SetNewUserPasswordRequest';
import type { TokenBlacklistRequest } from '../models/TokenBlacklistRequest';
import type { TokenObtainPair } from '../models/TokenObtainPair';
import type { TokenRefresh } from '../models/TokenRefresh';
import type { TokenRefreshRequest } from '../models/TokenRefreshRequest';
import type { TrenchBackupCodesSchema } from '../models/TrenchBackupCodesSchema';
import type { TrenchCodeSchemaRequest } from '../models/TrenchCodeSchemaRequest';
import type { TrenchDetailsSchema } from '../models/TrenchDetailsSchema';
import type { TrenchOptionalCodeSchemaRequest } from '../models/TrenchOptionalCodeSchemaRequest';
import type { ValidateRegistrationCodeRequest } from '../models/ValidateRegistrationCodeRequest';
import type { ValidateRegistrationCodeResponse } from '../models/ValidateRegistrationCodeResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AuthService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * @returns TrenchDetailsSchema
     * @throws ApiError
     */
    public authActivateCreate({
        method,
    }: {
        method: string,
    }): CancelablePromise<TrenchDetailsSchema> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/{method}/activate/',
            path: {
                'method': method,
            },
        });
    }

    /**
     * @returns TrenchBackupCodesSchema
     * @throws ApiError
     */
    public authActivateConfirmCreate({
        method,
        requestBody,
    }: {
        method: string,
        requestBody: TrenchCodeSchemaRequest,
    }): CancelablePromise<TrenchBackupCodesSchema> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/{method}/activate/confirm/',
            path: {
                'method': method,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public authDeactivateCreate({
        method,
        requestBody,
    }: {
        method: string,
        requestBody?: TrenchOptionalCodeSchemaRequest,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/{method}/deactivate/',
            path: {
                'method': method,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns ChangeUserPassword
     * @throws ApiError
     */
    public authChangePasswordCreate({
        requestBody,
    }: {
        requestBody: ChangeUserPasswordRequest,
    }): CancelablePromise<ChangeUserPassword> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/change-password',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public authCodeRequestCreate({
        requestBody,
    }: {
        requestBody?: MFAMethodCodeRequest,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/code/request/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PaginatedTrenchUserActiveMethodsSchemaList
     * @throws ApiError
     */
    public authMfaUserActiveMethodsList({
        limit,
        offset,
    }: {
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedTrenchUserActiveMethodsSchemaList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/mfa/user-active-methods/',
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * @returns PxtraUserPasswordReset
     * @throws ApiError
     */
    public authPasswordResetsRetrieve({
        id,
        userId,
    }: {
        id: string,
        userId: string,
    }): CancelablePromise<PxtraUserPasswordReset> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/password-resets',
            query: {
                'id': id,
                'user_id': userId,
            },
        });
    }

    /**
     * Registers the push token of the calling device.
     *
     * A token belongs to one account at a time, so registering it moves it to the
     * calling user.
     * @returns void
     * @throws ApiError
     */
    public authPushTokenCreate({
        requestBody,
    }: {
        requestBody: DevicePushTokenRequest,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/push-token',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any No response body
     * @throws ApiError
     */
    public authRecoveryRequestCreate({
        requestBody,
    }: {
        requestBody: RecoveryRequestRequest,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/recovery-request/',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                429: `No response body`,
            },
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public authRegisterCompleteCreate({
        requestBody,
    }: {
        requestBody: RegisterCompleteRequest,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/register/complete',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns RegisterEmailResponse
     * @throws ApiError
     */
    public authRegisterEmailCreate({
        requestBody,
    }: {
        requestBody: RegisterEmailRequest,
    }): CancelablePromise<RegisterEmailResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/register/email',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public authRegisterEmailConfirmCreate({
        requestBody,
    }: {
        requestBody: RegisterEmailConfirmRequest,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/register/email/confirm',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns ValidateRegistrationCodeResponse
     * @throws ApiError
     */
    public authRegisterValidateCodeCreate({
        requestBody,
    }: {
        requestBody?: ValidateRegistrationCodeRequest,
    }): CancelablePromise<ValidateRegistrationCodeResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/register/validate-code',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PasswordResetRequest
     * @throws ApiError
     */
    public authResetPasswordCreate({
        requestBody,
    }: {
        requestBody: PasswordResetRequestRequest,
    }): CancelablePromise<PasswordResetRequest> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/reset-password/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any No response body
     * @throws ApiError
     */
    public authSetNewPasswordCreate({
        requestBody,
    }: {
        requestBody: SetNewUserPasswordRequest,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/set-new-password',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns LoginFirstStepSchema
     * @throws ApiError
     */
    public authTokenCreate({
        requestBody,
        audience,
    }: {
        requestBody: LoginPushRequest,
        audience?: string,
    }): CancelablePromise<LoginFirstStepSchema> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/token/',
            query: {
                'audience': audience,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns LoginSecondStepSchema
     * @throws ApiError
     */
    public authTokenCodeCreate({
        requestBody,
        audience,
    }: {
        requestBody: CodeLoginPushRequest,
        audience?: string,
    }): CancelablePromise<LoginSecondStepSchema> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/token/code/',
            query: {
                'audience': audience,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Obtains a thirdparty openid-token and, if valid,
     * exchanges it for an authentication JSON web token
     * @returns ExtendedTokenObtain
     * @throws ApiError
     */
    public authTokenExchangeCodeCreate({
        requestBody,
    }: {
        requestBody: AuthCodeExchangeRequest,
    }): CancelablePromise<ExtendedTokenObtain> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/token/exchange/code',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns TokenObtainPair
     * @throws ApiError
     */
    public authTokenExchangeIdTokenCreate({
        requestBody,
    }: {
        requestBody: IdTokenExchangeRequest,
    }): CancelablePromise<TokenObtainPair> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/token/exchange/id-token',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Takes a token and blacklists it. Must be used with the
     * `rest_framework_simplejwt.token_blacklist` app installed.
     * @returns any No response body
     * @throws ApiError
     */
    public authTokenInvalidateCreate({
        requestBody,
    }: {
        requestBody: TokenBlacklistRequest,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/token/invalidate',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Takes a refresh type JSON web token and returns an access type JSON web
     * token if the refresh token is valid.
     * @returns TokenRefresh
     * @throws ApiError
     */
    public authTokenRefreshCreate({
        requestBody,
    }: {
        requestBody: TokenRefreshRequest,
    }): CancelablePromise<TokenRefresh> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/token/refresh/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
